import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlexControlledForm, Inscription, useDebounceState, useResponsive } from '@common-fe/common-fe';
import _ from 'lodash';

import useEmailVerificationFields, {
  FormValues,
} from '@/modules/user/components/Verification/hooks/useEmailVerificationFields';
import useSendVerificationCodeQuery from '@/modules/user/components/Verification/queries/useSendVerificationCode.query';
import {
  EmailVerificationStep,
  VerificationErrorText,
  VerificationServerError,
  VerificationType,
} from '@/modules/user/components/Verification/verification.types';
import useUpdateEmployeeQuery from '@/modules/user/queries/useUpdateEmployee.query';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';
import { useValidateForm } from '@/utils/hooks';

import VerificationModalWithWarning from '../components/VerificationModalWithWarning';

interface Props {
  email: string;
  employerProvidedEmail?: string;
  setEmail: (value: string) => void;
  onSetVisible: (value: boolean) => void;
  setStep: (step: EmailVerificationStep) => void;
}

const EnterEmailVerificationModal: React.FC<Props> = ({
  email,
  employerProvidedEmail,
  setEmail,
  onSetVisible,
  setStep,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { employee } = useEmployeeStore();
  const { updateEmployee } = useUpdateEmployeeQuery();
  const [state, handleSetState] = useState<FormValues>();
  const { values, setValues } = useDebounceState<FormValues>();
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const fields = useEmailVerificationFields({ email, errorText });
  const { handlePreSubmit, hasErrors } = useValidateForm(fields);
  const { sendVerificationCode, isLoading } = useSendVerificationCodeQuery();

  const isEmployerEmailUsed = useMemo(() => {
    const currentEmployeeEmail = values?.employeeProvidedEmail;
    return employerProvidedEmail && employerProvidedEmail === currentEmployeeEmail;
  }, [values, employerProvidedEmail]);

  const handleCancel = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const handleSendVerificationCode = useCallback(async () => {
    if (isEmployerEmailUsed && employee?.isVerifiedEmployerEmail) {
      await updateEmployee({ employee_provided_email: state?.employeeProvidedEmail || '' });
      setStep(EmailVerificationStep.EMAIL_VERIFIED_SUCCESSFULLY);
      return;
    }

    await sendVerificationCode({
      type: VerificationType.EMAIL,
      value: state?.employeeProvidedEmail || '',
    });
    setEmail(state?.employeeProvidedEmail || '');
    setStep(EmailVerificationStep.EMAIL_VERIFICATION_CODE);
  }, [
    isEmployerEmailUsed,
    sendVerificationCode,
    setEmail,
    setStep,
    updateEmployee,
    state?.employeeProvidedEmail,
    employee,
  ]);

  const handleNext = useCallback(async () => {
    setErrorText(undefined);
    try {
      const isSuccess = handlePreSubmit(state);
      if (!isSuccess) {
        return;
      }
      await handleSendVerificationCode();
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
      if (errorMessage.includes(VerificationServerError.EMAIL_ALREADY_VERIFIED)) {
        setErrorText(VerificationErrorText.EMAIL_ADDRESS_IS_ALREADY_VERIFIED);
        return;
      }

      if (errorMessage.includes(VerificationServerError.CODE_RESEND_TIMEOUT_ERROR)) {
        setErrorText(VerificationErrorText.CODE_RESEND_TIMEOUT_ERROR);
        return;
      }

      setErrorText(VerificationErrorText.OTHER_ERROR);
    }
  }, [handlePreSubmit, state, handleSendVerificationCode]);

  useEffect(() => {
    if (values) {
      handleSetState(values);
    }
  }, [handleSetState, values]);

  useEffect(() => {
    if (errorText === VerificationErrorText.EMAIL_ADDRESS_IS_ALREADY_VERIFIED) {
      setErrorText(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.employeeProvidedEmail]);

  return (
    <VerificationModalWithWarning
      title={t('Stay in the Know')}
      subTitle={t('Add your personal email and never miss an update.')}
      isLoading={isLoading}
      onSetVisible={onSetVisible}
      onCancel={handleCancel}
      onNext={handleNext}
      warningText={
        <Inscription>
          {t('To verify your email address we will send a verification code to the email address provided.')}
        </Inscription>
      }
      form={
        <FlexControlledForm
          fields={fields}
          formStyle={isMobile ? { margin: '12px' } : { margin: '24px' }}
          onChangeValues={setValues}
          showError={hasErrors}
          editMode
        >
          {isEmployerEmailUsed && (
            <Box
              margin={{ top: '-12px' }}
              style={
                isMobile
                  ? undefined
                  : {
                      display: 'grid',
                      gridTemplateColumns: 'minmax(200px, 1fr) 320px',
                    }
              }
            >
              <Box />
              <Inscription size={isMobile ? 'medium' : 'small'} color="textSecondary">
                {t('Employer provided email')}
              </Inscription>
            </Box>
          )}
        </FlexControlledForm>
      }
    />
  );
};

export default EnterEmailVerificationModal;
